import React, { createContext, useContext, useState, useCallback } from "react";
import { useGuideState } from "../hooks/useGuideState";
import type { GuideId, GuideContextValue } from "../types";

const GuideContext = createContext<GuideContextValue | undefined>(undefined);

interface GuideProviderProps {
  children: React.ReactNode;
}

export const GuideProvider: React.FC<GuideProviderProps> = ({ children }) => {
  const {
    completedGuides,
    onboardingDismissed,
    isFirstTimeUser,
    markGuideComplete,
    dismissOnboarding,
    resetProgress,
    isGuideComplete,
    getProgress,
  } = useGuideState();

  // Local state for UI interactions
  const [activeTour, setActiveTour] = useState<GuideId | null>(null);
  const [helpCenterOpen, setHelpCenterOpen] = useState(false);

  const startTour = useCallback((tourId: GuideId) => {
    setActiveTour(tourId);
    setHelpCenterOpen(false);
  }, []);

  const endTour = useCallback(() => {
    setActiveTour(null);
  }, []);

  const openHelpCenter = useCallback(() => {
    setHelpCenterOpen(true);
  }, []);

  const closeHelpCenter = useCallback(() => {
    setHelpCenterOpen(false);
  }, []);

  const handleMarkGuideComplete = useCallback(
    (guideId: GuideId) => {
      markGuideComplete(guideId);
      setActiveTour(null);
    },
    [markGuideComplete],
  );

  const resetGuideProgress = useCallback(() => {
    resetProgress();
    setActiveTour(null);
  }, [resetProgress]);

  const getGuideProgress = useCallback(() => {
    return getProgress();
  }, [getProgress]);

  const value: GuideContextValue = {
    // State
    completedGuides,
    activeTour,
    isFirstTimeUser,
    helpCenterOpen,
    onboardingDismissed,
    // Actions
    startTour,
    endTour,
    markGuideComplete: handleMarkGuideComplete,
    resetGuideProgress,
    openHelpCenter,
    closeHelpCenter,
    dismissOnboarding,
    // Queries
    isGuideComplete,
    getGuideProgress,
  };

  return (
    <GuideContext.Provider value={value}>{children}</GuideContext.Provider>
  );
};

/**
 * Hook to access the guide context
 * @throws Error if used outside GuideProvider
 */
export const useGuide = (): GuideContextValue => {
  const context = useContext(GuideContext);
  if (!context) {
    throw new Error("useGuide must be used within a GuideProvider");
  }
  return context;
};

/**
 * Hook to safely access guide context (returns undefined if not in provider)
 * Useful for components that may be used outside the guide system
 */
export const useGuideOptional = (): GuideContextValue | undefined => {
  return useContext(GuideContext);
};
